import React from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { addDays, format } from 'date-fns';
import DatePicker from 'react-datepicker';
import { Control, FieldErrors } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import * as z from 'zod';
import { hourOptions } from 'utils/shared/coachBuilder';
import { is24HourScale } from 'utils/shared/time/is24HourScale';
import Calendar from 'svg/Calendar';
import CheckIcon from 'svg/CheckIcon';
import ChevronDown from 'svg/ChevronDown';
import classNames from 'styles/utils/classNames';
import { whenAndWhereSchema } from '../types';

type Props = {
  selectStartTime: string;
  control: Control<z.infer<typeof whenAndWhereSchema>>;
  errors: FieldErrors<z.infer<typeof whenAndWhereSchema>>;
};

export default function WhenAndWhereTimeSlot({ selectStartTime, control, errors }: Props) {
  return (
    <div className="grid grid-cols-1 gap-6">
      <div>
        <label className="typography-product-subheading mb-3 block text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
          Select the date
        </label>
        <Controller
          control={control}
          name="selectDate"
          render={({ field: { onChange, value } }) => (
            <DatePicker
              wrapperClassName="w-full"
              minDate={new Date()}
              customInput={
                <div className="relative">
                  <input
                    readOnly
                    name="selectDate"
                    value={format(value || new Date(), 'MM/dd/yy')}
                    placeholder="Time slot"
                    className="h-11 w-full rounded-md border-0 bg-brand-gray-50 px-3 font-light text-color-text-lightmode-primary placeholder:text-color-text-lightmode-placeholder focus:outline-0 dark:text-color-text-darkmode-primary"
                  />
                  <Calendar className="pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 [&>path]:fill-color-text-lightmode-icon" />
                </div>
              }
              value={value.toISOString()}
              placeholderText="Time slot"
              onChange={onChange}
              selected={value}
            />
          )}
        />
        {errors?.selectDate?.message && (
          <p className="mt-2 text-sm text-color-text-lightmode-error dark:text-color-text-darkmode-error">
            {errors.selectDate.message}
          </p>
        )}
      </div>

      <div>
        <label
          htmlFor={selectStartTime}
          className="typography-product-subheading mb-3 block text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary"
        >
          Select start time
        </label>
        <Controller
          control={control}
          name="selectStartTime"
          render={({ field: { onChange, value } }) => {
            const startTime = hourOptions.find((option) => option.minutesAfterMidnight === value);
            const displayStartTime = is24HourScale() ? startTime?.display24 : startTime?.display12;
            return (
              <Listbox value={value} onChange={onChange}>
                {({ open }) => (
                  <div className="relative">
                    <Listbox.Button className="input-form relative h-11 w-full text-left">
                      <span className="block">{displayStartTime || 'Start time'}</span>
                      <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                        <ChevronDown className="h-4 text-gray-400" aria-hidden="true" />
                      </span>
                    </Listbox.Button>
                    <Transition
                      show={open}
                      as={React.Fragment}
                      leave="transition ease-in duration-100"
                      leaveFrom="opacity-100"
                      leaveTo="opacity-0"
                    >
                      <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-color-bg-lightmode-primary py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none dark:bg-color-bg-darkmode-primary">
                        {hourOptions.map((time) => (
                          <Listbox.Option
                            key={time.id}
                            className={({ active }) =>
                              classNames(
                                active
                                  ? 'bg-black text-white'
                                  : 'text-color-text-lightmode-primary dark:text-color-text-darkmode-primary',
                                'relative cursor-pointer select-none px-4 py-2',
                              )
                            }
                            value={time.minutesAfterMidnight}
                          >
                            {({ selected, active }) => (
                              <>
                                <span className="block">
                                  {is24HourScale() ? time.display24 : time.display12}
                                </span>
                                {selected && (
                                  <span
                                    className={classNames(
                                      active ? 'text-white' : 'text-black',
                                      'absolute inset-y-0 right-4 flex items-center',
                                    )}
                                  >
                                    <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                  </span>
                                )}
                              </>
                            )}
                          </Listbox.Option>
                        ))}
                      </Listbox.Options>
                    </Transition>
                  </div>
                )}
              </Listbox>
            );
          }}
        />
        {errors?.selectStartTime?.message && (
          <p className="mt-2 text-sm text-color-text-lightmode-error dark:text-color-text-darkmode-error">
            {errors.selectStartTime.message}
          </p>
        )}
      </div>
    </div>
  );
}
